import {
  deleteShippingSite,
  editShippingSite,
  addShippingSite,
} from "@/app/lib/actions/shipping-sites";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useShippingSitesQuery,
  invalidateShippingSitesQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addShippingSiteSchema,
  editShippingSiteSchema,
} from "@/lib/schemas/shipping-sites";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

type ShippingSite = {
  id: number;
  name: string;
  postalCode: string;
  isActive: boolean;
};

function ShippingSiteItemsListCard() {
  const queryClient = useQueryClient();

  const shippingSitesQuery = useShippingSitesQuery({ sort: { name: "asc" } });
  const handleCreate = async (
    shippingSite: z.infer<typeof addShippingSiteSchema>,
  ) => {
    const response = await addShippingSite(shippingSite);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau site de livraison créé.");
      invalidateShippingSitesQuery(queryClient);
    }
  };

  const handleUpdate = async (
    shippingSite: z.infer<typeof editShippingSiteSchema>,
  ) => {
    const response = await editShippingSite(shippingSite);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Site de livraison modifié.");
      invalidateShippingSitesQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteShippingSite(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Site de livraison supprimé.");
      invalidateShippingSitesQuery(queryClient);
    }
  };

  if (shippingSitesQuery.isPending) return <ItemsListCardSkeleton />;
  const shippingSites = shippingSitesQuery.data?.shippingSites ?? [];

  return (
    <ItemsListCard<ShippingSite>
      title="Sites de livraison"
      items={shippingSites}
      createSchema={addShippingSiteSchema}
      editSchema={editShippingSiteSchema}
      defaultCreateValues={{ name: "", postalCode: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="postalCode">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Code postal</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={5}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { ShippingSiteItemsListCard };
